import { CSSProperties, ReactElement, memo } from "react";
import { List } from "../list";
import { isEqual } from "libs/predicates";
import {
  EntryActions,
  entryCSSClasses,
  EntryTimestamp,
  OtherCommandEntryAction,
  PrivateEntryIcon,
  Recipients,
  StarredEntryIcon,
  Summary,
  useShouldShowChannelLabels,
} from "./layout";
import { NotificationTimestamp } from "./NotificationEntry";
import { isModKeyActive } from "~/environment/command.service";
import { Avatar } from "../Avatar";
import { useMessage } from "~/hooks/useMessage";
import { useThread } from "~/hooks/useThread";
import { PointerWithRecord, RecordValue } from "libs/schema";
import { useNotification } from "~/hooks/useNotification";
import { LabelContainer, PermittedGroupChips, ThreadLabelsCountChip } from "../LabelChip";
import { useMessageRecipientNames } from "~/hooks/useMessageRecipientNames";
import { useMessageSender } from "~/hooks/useMessageSender";
import { useAsPointerWithRecord } from "~/hooks/useAsPointerWithRecord";
import { Tooltip } from "../Tooltip";
import { MdScheduleSend, MdSync, MdSyncProblem } from "react-icons/md";
import { cx } from "@emotion/css";
import { useIsMessageSent } from "~/hooks/useIsMessageSent";
import { useIsOnline } from "~/hooks/useIsOnline";
import { useTimeFromNow } from "~/hooks/useTimeFromNow";
import { useSoonestScheduledMessageForThread } from "~/hooks/useSoonestScheduledMessageForThread";
import { throwUnreachableCaseError } from "libs/errors";
import { ParentComponent } from "~/utils/type-helpers";
import { ClientEnvironment } from "~/environment/ClientEnvironment";
import { map } from "rxjs";
import { combineLatest } from "rxjs";
import { of } from "rxjs";
import { switchMap } from "rxjs";
import { ClientRecordLoaderObserveGetRecordResult } from "~/environment/RecordLoader";
import { ObserveOptions } from "~/environment/RecordLoader";
import { Observable } from "rxjs";
import { ClientRecordLoaderObserveQueryResult } from "~/environment/RecordLoader";
import { MdCloudUpload } from "react-icons/md";
import { config } from "~/environment/config";

export function onMessageSelectNavigateToThread(
  environment: Pick<ClientEnvironment, "router">,
  props: {
    event: React.MouseEvent | React.KeyboardEvent | MouseEvent | KeyboardEvent;
    threadRoutePrefix?: string;
    threadId: string;
    messageId: string;
  },
) {
  let threadRoutePrefix = props.threadRoutePrefix || "";

  if (!threadRoutePrefix.endsWith("/")) {
    threadRoutePrefix += "/";
  }

  return environment.router.navigate(`${threadRoutePrefix}threads/${props.threadId}?message=${props.messageId}`, {
    openInNewTab: isModKeyActive(props.event),
  });
}

/**
 * Because we're using a virtual list, entries are positioned based on their index.
 * If one of the list entry components renders `null`, then it will create a gap in
 * the list since the next entry will have a higher index. For this reason, we need
 * to filter out any messages that will not be rendered from the list.
 */
export function observeFilteredMessageEntriesForVirtualList(
  environment: Pick<ClientEnvironment, "recordLoader">,
  observable: Observable<ClientRecordLoaderObserveQueryResult<"message">>,
  options?: ObserveOptions,
): Observable<ClientRecordLoaderObserveQueryResult<"message">> {
  const loader = environment.recordLoader;

  return observable.pipe(
    switchMap(([messages, meta]) => {
      const observable: Observable<
        Array<[RecordValue<"message">, ClientRecordLoaderObserveGetRecordResult<"thread">]>
      > =
        messages.length === 0 ?
          of([])
        : combineLatest(
            messages.map((m) => combineLatest([of(m), loader.observeGetRecord("thread", m.thread_id, options)])),
          );

      return observable.pipe(
        map((results) => {
          const messages = results.filter(([_, [thread]]) => !!thread).map(([message]) => message);

          return [messages, meta] satisfies ClientRecordLoaderObserveQueryResult<"message">;
        }),
      );
    }),
  );
}

export const MessageEntry: ParentComponent<{
  messageId: string;
  relativeOrder: number;
  /**
   * Whether the "scheduled to be sent icon" should be shown only if a message matching the
   * provided messageId is scheduled to be sent or if a thread matching the provided messageId
   * has any messages which are scheduled to be sent.
   */
  showScheduledToBeSentIconFor: "messageId" | "threadId";
  showRecipientNames?: boolean;
  overrideSubject?: string | ReactElement;
  overrideBody?: string | ReactElement;
  style?: CSSProperties;
}> = memo(
  ({
    messageId,
    showRecipientNames = false,
    relativeOrder,
    overrideSubject,
    overrideBody,
    showScheduledToBeSentIconFor,
    style,
  }) => {
    const showLabels = useShouldShowChannelLabels();
    const [message] = useMessage(messageId);
    const [thread] = useThread(message?.thread_id);
    const [notification] = useNotification({ threadId: message?.thread_id });
    const sender = useMessageSender(messageId);
    const entryData = useAsPointerWithRecord("message", message);

    if (!message || !thread || !entryData) return null;

    const isPrivateThread = thread.visibility === "PRIVATE";
    const senderLabel = sender?.label || (sender.isLoading ? "loading..." : "unknown");

    return (
      <List.Entry<PointerWithRecord<"message">> id={messageId} data={entryData} relativeOrder={relativeOrder}>
        <div role="listitem" className={cx(entryCSSClasses, "MessageEntry")} style={style}>
          <div className="pr-2 sm-w:pr-3">
            <Avatar label={senderLabel} photoURL={sender?.photoUrl} width="30px" />
          </div>

          <div className="flex flex-col md-w:flex-row flex-1 min-w-0">
            <Recipients
              nonTruncatedSuffix={
                <>
                  {isPrivateThread && <PrivateEntryIcon />}
                  {notification?.is_starred && <StarredEntryIcon />}
                </>
              }
            >
              {showRecipientNames ?
                <div className="truncate">
                  <RecipientNames messageId={messageId} />
                </div>
              : <>
                  <span className="mr-3 truncate">{senderLabel}</span>
                </>
              }
            </Recipients>

            <Summary
              subject={overrideSubject ?? thread.subject}
              formatAsReply={message.is_reply && message.type === "COMMS"}
              details={isPrivateThread ? "private message" : (overrideBody ?? message.body_text)}
            />
          </div>

          <div className="mx-4 flex space-x-3">
            <ScheduledToBeSentIcons message={message} showScheduledToBeSentIconFor={showScheduledToBeSentIconFor} />
          </div>

          <LabelContainer>
            {showLabels && <PermittedGroupChips threadId={message.thread_id} />}
            {showLabels && <ThreadLabelsCountChip threadId={message.thread_id} />}
          </LabelContainer>

          <EntryActions
            defaultComponent={
              notification?.has_reminder ?
                <NotificationTimestamp notification={notification} sentAt={message.sent_at} />
              : <EntryTimestamp datetime={message.sent_at} />
            }
          >
            <OtherCommandEntryAction />
          </EntryActions>
        </div>
      </List.Entry>
    );
  },
  isEqual,
);

/* -----------------------------------------------------------------------------------------------*/

const RecipientNames: ParentComponent<{ messageId: string }> = (props) => {
  const [names, { isLoading }] = useMessageRecipientNames(props.messageId);

  return <>{names.join(", ") || (isLoading ? "loading..." : "unknown")}</>;
};

/* -------------------------------------------------------------------------------------------------
 *  ScheduledToBeSentIcons
 * -------------------------------------------------------------------------------------------------
 */

export const ScheduledToBeSentIcons: ParentComponent<{
  message: Pick<
    RecordValue<"message">,
    "thread_id" | "sent_at" | "last_edited_at" | "server_updated_at" | "is_delivered"
  >;
  /**
   * Whether the "scheduled to be sent icon" should be shown only if a message matching the
   * provided messageId is scheduled to be sent or if a thread matching the provided messageId
   * has any messages which are scheduled to be sent.
   */
  showScheduledToBeSentIconFor: "messageId" | "threadId";
}> = (props) => {
  const isOnline = useIsOnline();

  const [soonestScheduledMessageForThread] = useSoonestScheduledMessageForThread({
    threadId: props.showScheduledToBeSentIconFor === "threadId" ? props.message.thread_id : undefined,
  });

  const message =
    props.showScheduledToBeSentIconFor === "messageId" ? props.message
    : props.showScheduledToBeSentIconFor === "threadId" ? soonestScheduledMessageForThread
    : throwUnreachableCaseError(props.showScheduledToBeSentIconFor);

  const scheduledToBeSentAt = useTimeFromNow({ timestamp: message?.sent_at });

  const canCancelSendingMessage = !useIsMessageSent(message);

  if (!message) return null;

  const isCreationSyncedWithServer = isMessageCreationSyncedWithServer(message);
  const isEditSyncedWithServer = isMessageEditSyncedWithServer(message);

  const isAnIconOtherThanIsProcessingShown =
    !isCreationSyncedWithServer || (isCreationSyncedWithServer && !isEditSyncedWithServer) || canCancelSendingMessage;

  const isProcessing = !isAnIconOtherThanIsProcessingShown && !message.is_delivered;

  return (
    <>
      {!isCreationSyncedWithServer && (
        <Tooltip
          side="bottom"
          // We're choosing to use the language "uploading message" instead of "sending message" because a message
          // isn't actually "sent" until it's "scheduled to be sent" time has passed.
          content={isOnline ? `Uploading message ...` : `Waiting for network connection to upload this message`}
        >
          {isOnline ? onlineIcon : offlineIcon}
        </Tooltip>
      )}

      {isCreationSyncedWithServer && !isEditSyncedWithServer && (
        <Tooltip
          side="bottom"
          content={isOnline ? `Uploading edits...` : `Waiting for network connection to upload edits`}
        >
          {isOnline ? onlineIcon : offlineIcon}
        </Tooltip>
      )}

      {canCancelSendingMessage && (
        <Tooltip
          side="bottom"
          content={`This message is scheduled to be sent ${scheduledToBeSentAt}. You can cancel it before then.`}
        >
          {scheduledToBeSentIcon}
        </Tooltip>
      )}

      {
        // Hiding this in production since I think it might be too much information for the user.
        // Despite the message, I can imagine folks being confused about whether or not the message has
        // been synced to the server and if it's safe for them to quit the app. Additionally, it currently
        // takes a while for the server to pick up a message for processing and showing this icon could
        // make the app feel slow.
        //
        // Useful to know in development though.
        config.appEnvironment !== "production" && isProcessing && (
          <Tooltip
            side="bottom"
            content={`This message has been uploaded and is being processed by the server. Some of its changes may not be reflected yet.`}
          >
            {processingIcon}
          </Tooltip>
        )
      }
    </>
  );
};

export function isMessageCreationSyncedWithServer(message: Pick<RecordValue<"message">, "server_updated_at">) {
  return !!message.server_updated_at;
}

export function isMessageEditSyncedWithServer(
  message: Pick<RecordValue<"message">, "server_updated_at" | "last_edited_at">,
) {
  return (
    !!message.server_updated_at && (!message.last_edited_at || message.last_edited_at <= message.server_updated_at)
  );
}

const scheduledToBeSentIconCSS = cx(
  "flex justify-center items-center",
  "hover:cursor-pointer text-slate-8 scale-125",
  "hover:text-black",
);

const scheduledToBeSentIcon = (
  <span className={cx(scheduledToBeSentIconCSS, "mr-1")}>
    <MdScheduleSend />
  </span>
);

const onlineIcon = (
  <span className={cx(scheduledToBeSentIconCSS, "mr-1")}>
    <MdSync />
  </span>
);

const offlineIcon = (
  <span className={cx(scheduledToBeSentIconCSS, "mr-1")}>
    <MdSyncProblem />
  </span>
);

const processingIcon = (
  <span className={cx(scheduledToBeSentIconCSS, "mr-1")}>
    <MdCloudUpload />
  </span>
);

/* -----------------------------------------------------------------------------------------------*/
